/**
 * The address of a finance document's own screen, and the one flag that
 * opens it with its Invoice Papers already beside it.
 *
 * ClickUp 2026-10-23, "Please add the double screen functionality everywhere
 * there are approvals": the Invoice Papers pane sits on every invoice-kind
 * screen (RelatedDocumentsPane). An approver who arrives from an approvals
 * list (bunker invoice approvals, the credit approval screen's invoice lists)
 * should not have to find the button, so those lists link here with
 * ``withPapers`` and the invoice screen opens with the pane showing.
 *
 * One home for two rules, used by the approvals list and the credit approval
 * screen:
 *   - which path opens which kind of document (the six kinds the pane knows,
 *     the same words as the server's ``INVOICE_KINDS``);
 *   - the ``papers=1`` flag, read back by ``useInvoicePapersOpen``.
 * Older screens (the invoice lists, InvoicePanel, the partner's Financial
 * Accounting tab, about 30 sites) still spell their own per-kind paths: debt,
 * to be folded in on their own, not with a feature.
 *
 * A kind this map does not know returns ``null``: the caller shows plain text
 * instead of a link that would open the wrong screen. Never guess from a label.
 */
import { RETURN_TO_PARAM } from '../components/shared/useDocumentReturn';

/** Query-string key: ``papers=1`` opens the screen with its Invoice Papers shown. */
export const INVOICE_PAPERS_PARAM = 'papers';

export const INVOICE_SCREEN_PATHS = Object.freeze({
  incoming: '/invoicing/incoming',
  outgoing: '/invoicing/outgoing',
  proforma: '/invoicing/proforma',
  'purchase-order': '/invoicing/purchase-orders',
  'credit-note': '/invoicing/credit-notes',
  'debit-note': '/invoicing/debit-notes',
});

/**
 * The kinds whose screens admit Financials only (the router gates
 * ``/invoicing/credit-notes/:id`` and ``/invoicing/debit-notes/:id`` with
 * requiresFinancialAccess; the other four take the delivery-scoped tier too).
 * The credit approval screen's server answer mirrors this split
 * (``can_open_invoices`` / ``can_open_notes``).
 */
export const FINANCE_ONLY_KINDS = Object.freeze(['credit-note', 'debit-note']);

/**
 * @param {string} kind       one of the keys of ``INVOICE_SCREEN_PATHS``
 * @param {number|string} id  the document's id
 * @param {Object}  [options]
 * @param {boolean} [options.withPapers]  open with the Invoice Papers beside it
 * @param {string}  [options.returnTo]    the screen the Back arrow goes to
 * @returns {string|null} the address, or ``null`` for a kind with no screen here
 */
export const invoiceScreenPath = (kind, id, { withPapers = false, returnTo = null } = {}) => {
  // Own keys only: an inherited name ('constructor') is not a kind.
  const base = Object.prototype.hasOwnProperty.call(INVOICE_SCREEN_PATHS, kind)
    ? INVOICE_SCREEN_PATHS[kind]
    : null;
  if (!base || id === null || id === undefined || id === '') return null;
  const params = new URLSearchParams();
  if (returnTo) params.set(RETURN_TO_PARAM, returnTo);
  if (withPapers) params.set(INVOICE_PAPERS_PARAM, '1');
  const query = params.toString();
  return `${base}/${id}${query ? `?${query}` : ''}`;
};

/** Did this screen's address ask for the Invoice Papers to be open on arrival? */
export const arrivesWithInvoicePapers = (search) => (
  new URLSearchParams(search || '').get(INVOICE_PAPERS_PARAM) === '1'
);
